<article
  class="video-card"
  [ngClass]="statusClass()"
  (click)="onClick()"
>
  <header class="video-card__header">
    <div class="video-card__icon-wrapper">
      <span class="material-icons">{{ iconName() }}</span>
    </div>

    <div class="video-card__info">
      <h3 class="video-card__title" [title]="status().title">{{ status().title }}</h3>
      <div class="video-card__meta">
        <span class="date">{{ formatDateMadrid(status().created_at, false, false) }}</span>
        <span class="separator">•</span>
        <span class="elapsed">{{ timeElapsedText() }}</span>
      </div>
    </div>
  </header>

  <div class="video-card__body">
    <div class="current-status-row">
      <span class="status-badge" [ngClass]="currentStatusBadge().className">
        {{ currentStatusBadge().label }}
      </span>
      @if (lastStatusTimestamp()) {
        <span class="last-update">
          Último estado: {{ formatDateMadrid(lastStatusTimestamp(), false, true) }}
        </span>
      }
    </div>

    @if (isQueued()) {
      <div class="queue-details">
        <span class="chip">Cola: {{ queueTypeLabel() }}</span>
        @if (status().queue_position) {
          <span class="chip">{{ queuePositionLabel() }}: {{ status().queue_position }}</span>
        }
        @if (globalQueuePosition()) {
          <span class="chip">Posición global: {{ globalQueuePosition() }}</span>
        }
        @if (isReprocess()) {
          <span class="chip">Reprocesado</span>
        }
      </div>
    }

    <div class="progress-section">
      <div class="progress-track">
        <div
          class="progress-fill"
          [class.indeterminate]="isIndeterminate()"
          [style.width]="progressBarWidth()">
        </div>
      </div>

      <div class="status-row">
        <span class="status-text">{{ statusLabel() }}</span>
        @if (status().ia_progress_percentage) {
          <span class="percentage-text">{{ status().ia_progress_percentage | number:'1.0-0' }}%</span>
        }
      </div>

      @if (etaLabel() && etaRangeText()) {
        <div class="eta-row">
          <span class="eta-label">{{ etaLabel() }}:</span>
          <span class="eta-value">{{ etaRangeText() }}</span>
        </div>
      }
    </div>

    @if (status().is_error) {
      <div class="error-box">
        <span class="material-icons">warning</span>
        <p>{{ status().error_message || 'Error desconocido en el procesamiento.' }}</p>
      </div>
    }
  </div>

  <footer class="video-card__footer">
    <button
      class="text-button"
      (click)="toggleTimeline($event)">
      <span class="material-icons">
        {{ isTimelineExpanded() ? 'expand_less' : 'history' }}
      </span>
      {{ isTimelineExpanded() ? 'Ocultar historial' : 'Ver historial' }}
    </button>

    <div class="footer-actions">
      @if (canRetry() && isQueued()) {
        <button class="priority-button" (click)="onPrioritize($event)">
          <span class="material-icons">priority_high</span>
          Priorizar
        </button>
      }

      @if (canRetry()) {
        <button class="retry-button" (click)="onRetry($event)">
          <span class="material-icons">refresh</span>
          Reintentar
        </button>

        <button class="archive-button" (click)="onArchive($event)">
          <span class="material-icons">archive</span>
          Archivar
        </button>
      }
    </div>
  </footer>

  @if (isTimelineExpanded()) {
    <div class="timeline-container">
      @if (showProcessingStages() && processingRuns().length > 0) {
        <div class="stages-container">
          <div class="stages-header">
            <p class="stages-title">Stages de procesamiento</p>
          </div>

          @if (processingRuns().length > 1) {
            <p class="run-meta">
              Mostrando por defecto el último procesamiento. Puedes seleccionar otro desde el historial.
            </p>
          }

          @if (selectedProcessingRun()?.startedAt) {
            <p class="run-meta">
              Inicio del intento: {{ formatDateMadrid(selectedProcessingRun()?.startedAt, true, true) }}
            </p>
          }
          @if (selectedRunTypeLabel()) {
            <p class="run-meta">
              Tipo: {{ selectedRunTypeLabel() }}
            </p>
          }

          @if (stageTree().length === 0) {
            <p class="stages-empty">Este procesamiento no reportó stages.</p>
          } @else {
            @for (stage of stageTree(); track stage.name) {
              <div class="stage-item">
                <div class="stage-head">
                  <span class="stage-name">{{ stage.name }}</span>
                  <div class="stage-right">
                    @if (formatStageDuration(stage)) {
                      <span class="stage-duration">{{ formatStageDuration(stage) }}</span>
                    }
                    <span class="stage-status" [class.error]="stage.status === 'ERROR'">
                      {{ formatStageStatus(stage.status) }}
                    </span>
                  </div>
                </div>
                <div class="stage-times">
                  @if (stage.started_at) {
                    <span>Inicio: {{ formatDateMadrid(stage.started_at, true, true) }}</span>
                  }
                  @if (stage.completed_at) {
                    <span>Fin: {{ formatDateMadrid(stage.completed_at, true, true) }}</span>
                  }
                </div>
                @if (stage.sub_stages?.length > 0) {
                  <div class="sub-stage-list">
                    @for (sub of stage.sub_stages; track sub.name) {
                      <div class="sub-stage-item">
                        <span class="sub-stage-name">{{ sub.name }}</span>
                        <div class="sub-stage-right">
                          @if (formatStageDuration(sub)) {
                            <span class="sub-stage-duration">{{ formatStageDuration(sub) }}</span>
                          }
                          <span class="sub-stage-status" [class.error]="sub.status === 'ERROR'">
                            {{ formatStageStatus(sub.status) }}
                          </span>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            }
          }
        </div>
      } @else if (!showProcessingStages() && status().current_status_type === 'PROCESSING') {
        <div class="stages-container stages-container--restricted">
          <p class="stages-title">Stages de procesamiento</p>
          <p class="stages-empty">
            Visible solo para usuarios de IAsolver.
          </p>
        </div>
      }

      @for (event of status().history_log; track event.timestamp) {
        <div
          class="timeline-event"
          [class.current]="$last"
          [class.timeline-event--selectable]="isTimelineRunSelectable(event)"
          [class.timeline-event--selected]="isTimelineRunSelected(event)"
          (click)="selectRunFromTimeline($event, event)">

          <div class="timeline-marker-col">
            @if (!$last) {
              <div class="timeline-line"></div>
            }
            <div class="timeline-dot"></div>
          </div>

          <div class="timeline-content">
            <span class="event-time">{{ formatDateMadrid(event.timestamp, true, true) }}</span>
            <span class="event-label">{{ formatTimelineLabel(event.label) }}</span>
            <span class="event-stage">{{ formatTimelineStage(event.stage) }}</span>
            @if (isTimelineRunSelectable(event) && showProcessingStages()) {
              <button type="button" class="timeline-run-link">
                {{ getTimelineRunLinkLabel(event) }}
              </button>
            }
            @if (event.metadata && event.metadata['message']) {
              <p class="event-msg">{{ event.metadata['message'] }}</p>
            }
          </div>
        </div>
      }
    </div>
  }
</article>
